import * as React from 'react'
import useEmblaCarousel, {
  type UseEmblaCarouselType,
} from 'embla-carousel-react'
import { ChevronLeft, ChevronRight } from 'lucide-react'

import { cn } from '@ui/lib/utils'
import { Button, ButtonProps } from '@ui/components/ui/button'
import { cva } from 'class-variance-authority'

type CarouselApi = UseEmblaCarouselType[1]
type UseCarouselParameters = Parameters<typeof useEmblaCarousel>
type CarouselOptions = UseCarouselParameters[0]
type CarouselPlugin = UseCarouselParameters[1]

export type CarouselProps = {
  opts?: CarouselOptions
  plugins?: CarouselPlugin
  orientation?: 'horizontal' | 'vertical'
  setApi?: (api: CarouselApi) => void
}

type CarouselContextProps = {
  carouselRef: ReturnType<typeof useEmblaCarousel>[0]
  api: ReturnType<typeof useEmblaCarousel>[1]
  scrollPrev: () => void
  scrollNext: () => void
  canScrollPrev: boolean
  canScrollNext: boolean
} & CarouselProps

type CarouselArrowProps = Omit<ButtonProps, 'variant'> & {
  variant?: 'fullHeight' | 'bottom'
}

const arrowVariants = cva('', {
  variants: {
    variant: {
      fullHeight:
        'absolute bg-alpha-white-90 top-[0] h-[calc(100%-35px)] w-[32px] border-0 rounded-none transform-none text-gray-700 [&>svg]:text-gray-700 [&>svg]:size-[37px] [&>svg]:stroke-[3px]',
      bottom:
        'size-xxs bottom-[0] rounded-[2px] bg-alpha-black-50 disabled:opacity-1 hover:bg-alpha-black-50 [&:disabled>svg]:text-alpha-black-20 [&>svg]:text-white [&>svg]:size-5',
    },
    position: {
      left: '',
      right: '',
    },
  },
  compoundVariants: [
    {
      variant: 'fullHeight',
      position: 'left',
      class: 'rounded-tl-[6px] left-0',
    },
    {
      variant: 'fullHeight',
      position: 'right',
      class: 'rounded-tr-[6px] right-0',
    },
    {
      variant: 'bottom',
      position: 'left',
      class: 'mr-[6px]',
    },
  ],
})

const CarouselContext = React.createContext<CarouselContextProps | null>(null)

export const useCarousel = () => {
  const context = React.useContext(CarouselContext)

  if (!context) {
    throw new Error('useCarousel must be used within a <Carousel />')
  }

  return context
}

const Carousel = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & CarouselProps
>(
  (
    {
      orientation = 'horizontal',
      opts,
      setApi,
      plugins,
      className,
      children,
      ...props
    },
    ref,
  ) => {
    const [carouselRef, api] = useEmblaCarousel(
      {
        ...opts,
        axis: orientation === 'horizontal' ? 'x' : 'y',
      },
      plugins,
    )
    const [canScrollPrev, setCanScrollPrev] = React.useState(false)
    const [canScrollNext, setCanScrollNext] = React.useState(false)

    const onSelect = React.useCallback((api: CarouselApi) => {
      if (!api) {
        return
      }

      setCanScrollPrev(api.canScrollPrev())
      setCanScrollNext(api.canScrollNext())
    }, [])

    const scrollPrev = React.useCallback(() => {
      api?.scrollPrev()
    }, [api])

    const scrollNext = React.useCallback(() => {
      api?.scrollNext()
    }, [api])

    const handleKeyDown = React.useCallback(
      (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault()
          scrollPrev()
        } else if (event.key === 'ArrowRight') {
          event.preventDefault()
          scrollNext()
        }
      },
      [scrollPrev, scrollNext],
    )

    React.useEffect(() => {
      if (!api || !setApi) {
        return
      }

      setApi(api)
    }, [api, setApi])

    React.useEffect(() => {
      if (!api) {
        return
      }

      onSelect(api)
      api.on('reInit', onSelect)
      api.on('select', onSelect)

      return () => {
        api?.off('select', onSelect)
      }
    }, [api, onSelect])

    return (
      <CarouselContext.Provider
        value={{
          carouselRef,
          api: api,
          opts,
          orientation:
            orientation || (opts?.axis === 'y' ? 'vertical' : 'horizontal'),
          scrollPrev,
          scrollNext,
          canScrollPrev,
          canScrollNext,
        }}
      >
        <div
          ref={ref}
          onKeyDownCapture={handleKeyDown}
          className={cn('relative', className)}
          role="region"
          aria-roledescription="carousel"
          {...props}
        >
          {children}
        </div>
      </CarouselContext.Provider>
    )
  },
)
Carousel.displayName = 'Carousel'

const CarouselContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  const { carouselRef, orientation } = useCarousel()

  return (
    <div ref={carouselRef}>
      <div
        ref={ref}
        className={cn(
          'flex',
          orientation === 'horizontal' ? '' : '-mt-s flex-col',
          className,
        )}
        {...props}
      />
    </div>
  )
})
CarouselContent.displayName = 'CarouselContent'

type CarouselItemProps = {
  sideGap?: number
  isFullPreview?: boolean
} & React.HTMLAttributes<HTMLDivElement>

const CarouselItem = React.forwardRef<HTMLDivElement, CarouselItemProps>(
  ({ className, sideGap, isFullPreview, ...props }, ref) => {
    const { orientation } = useCarousel()

    return (
      <div
        ref={ref}
        role="group"
        aria-roledescription="slide"
        style={{
          ...(isFullPreview && { width: '100%' }),
          marginRight: sideGap && `${sideGap}px`,
          maxWidth: sideGap && `calc(100% - ${sideGap}px)`,
        }}
        className={cn(
          'min-w-0 shrink-0 grow-0',
          orientation === 'horizontal' ? !sideGap && 'pr-2.5' : 'pt-s',
          className,
        )}
        {...props}
      />
    )
  },
)
CarouselItem.displayName = 'CarouselItem'

const CarouselPrevious = React.forwardRef<
  HTMLButtonElement,
  CarouselArrowProps
>(({ className, variant = 'fullHeight', size = 'icon_m', ...props }, ref) => {
  const { scrollPrev, canScrollPrev, opts } = useCarousel()

  const styles = cn(
    ((variant === 'fullHeight' && !canScrollPrev) || !opts?.active) && 'hidden',
    arrowVariants({ variant, position: 'left' }),
    className,
  )

  return (
    <Button
      ref={ref}
      variant="primary"
      size={size}
      className={styles}
      disabled={!canScrollPrev}
      onClick={scrollPrev}
      {...props}
    >
      <ChevronLeft />
      <span className="sr-only">Previous slide</span>
    </Button>
  )
})
CarouselPrevious.displayName = 'CarouselPrevious'

const CarouselNext = React.forwardRef<HTMLButtonElement, CarouselArrowProps>(
  ({ className, variant = 'fullHeight', size = 'icon_m', ...props }, ref) => {
    const { scrollNext, canScrollNext, opts } = useCarousel()

    const styles = cn(
      ((variant === 'fullHeight' && !canScrollNext) || !opts?.active) &&
        'hidden',
      arrowVariants({ variant, position: 'right' }),
      className,
    )

    return (
      <Button
        ref={ref}
        variant="primary"
        size={size}
        className={styles}
        disabled={!canScrollNext}
        onClick={scrollNext}
        {...props}
      >
        <ChevronRight />
        <span className="sr-only">Next slide</span>
      </Button>
    )
  },
)
CarouselNext.displayName = 'CarouselNext'

export {
  type CarouselApi,
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
}
